/* eslint-disable no-restricted-imports */
import { useFormik } from 'formik';
import { useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { object, string } from 'yup';

import { type LiteQuote, useGetShippingRates } from '@frontend/api';
import { SHIPPING_METHOD } from '@frontend/api';
import { SHIPPING_METHOD_LABEL } from '@frontend/api';
import { toMoment } from '@frontend/utils';

import Spinner from 'components/Spinner/Spinner';
import Radio from 'components/lib/Radio/Radio';

import {
  PAYMENT_METHOD,
  SHIPPING_CARRIER,
  SHIPPING_GROUPING,
  SHIPPING_TYPE,
} from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';

import { formatPrice } from 'utils/formatters';

import { setShippingChoice, setShippingRate } from '../../data/actions';
import {
  getPaymentMethod,
  getShippingAccount,
  getShippingInformationType,
  getShippingType,
  getSubtotalCost,
  getTaxCost,
  getTotalCost,
  getIsFetchingShippingRates,
  getFacility,
  getShippingInformation,
  getSelectedOptions,
} from '../../data/reducer';
import {
  SHIPPING_INFO_TYPES,
  SHIPPING_METHOD_RATES,
  SHIPPING_TYPE_LABEL,
} from '../constants';
import PaymentSummary from './PaymentSummary';
import ShippingAccountForm from './ShippingAccountForm';

const RadioGroup = Radio.Group;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.value === PAYMENT_METHOD.PURCHASE_ORDER && 'padding-bottom: 22px;'};
`;

const StyledRadio = styled(Radio)`
  font-size: 13px;
  margin-bottom: 10px;
`;

const DeliveryOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 22px;
  padding-top: 22px;
  width: 100%;
`;

const DeliveryOptions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const DeliveryOptionsHeader = styled.div`
  color: #9b9b9b;
  font-family: 'Libre Franklin';
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 13px;
  text-transform: uppercase;
`;

const ShippingRatePrice = styled.span`
  padding-left: 24px;
`;

const TotalsWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LabelCell = styled.td`
  font-weight: bold;
  text-align: right;
`;

const ValueCell = styled.td`
  padding-left: 19px;
  text-align: right;
`;

const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
type ShippingOptionsFormProps = {
  quote: LiteQuote;
};

const ShippingOptionsForm = forwardRef<
  HTMLFormElement,
  ShippingOptionsFormProps
>(({ quote }, ref) => {
  const dispatch = useDispatch();

  const isCalculatedShippingEnabled = useFeatureFlagValue(
    featureFlagKeys.isCalculatedShippingEnabled,
  );
  const isFetchingShippingRates = useSelector(getIsFetchingShippingRates);
  const shippingAccount = useSelector(getShippingAccount);

  const paymentMethod = useSelector(getPaymentMethod);
  const shippingInformationType = useSelector(getShippingInformationType);
  const shippingType = useSelector(getShippingType);
  const shippingInformation = useSelector(getShippingInformation);
  const facility = useSelector(getFacility);
  const selectedOptions = useSelector(getSelectedOptions);
  const shippingAddress =
    shippingInformationType === SHIPPING_INFO_TYPES.ONE_TIME_ADDRESS
      ? shippingInformation
      : facility;
  const subtotalCost = useSelector(getSubtotalCost);
  const taxRate = useSelector((state) => getTaxCost(state, quote));
  const totalCost = useSelector((state) => getTotalCost(state, quote));

  const setChoice = useCallback(
    (choice: any) => {
      dispatch(setShippingChoice(choice));
    },
    [dispatch],
  );

  const setRate = useCallback(
    (rate: any) => {
      dispatch(setShippingRate(rate));
    },
    [dispatch],
  );

  const {
    values,
    setFieldValue,
    submitForm,
    handleChange,
    handleSubmit,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      shippingType: shippingType === undefined ? null : shippingType,
      shippingRate: SHIPPING_METHOD_RATES[0],
      customerAccount: shippingAccount?.customersAccountNumber ?? '',
      customerCarrier:
        shippingAccount?.customersCarrier ?? SHIPPING_CARRIER.UPS,
      method:
        shippingAccount?.shippingMethod ?? SHIPPING_METHOD.EARLY_OVERNIGHT,
      grouping: shippingAccount?.shipWhen ?? SHIPPING_GROUPING.BULK,
    },
    onSubmit: (values) => {
      const {
        customerAccount,
        customerCarrier,
        method,
        grouping,
        shippingRate,
        shippingType,
      } = values;
      if (values.shippingType === null) {
        setChoice({
          shippingAccount: undefined,
          shippingType: values.shippingType,
          shippingRate: undefined,
        });
      } else if (values.shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT) {
        const shippingAccount = {
          customersAccountNumber: customerAccount,
          customersCarrier: customerCarrier,
          shippingMethod: method,
          shipWhen: grouping,
        };
        setChoice({
          shippingAccount,
          shippingType,
          shippingRate: undefined,
        });
      } else
        setChoice({
          shippingAccount: undefined,
          shippingType,
          shippingRate:
            shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT
              ? shippingRate
              : undefined,
        });
    },
    validationSchema: object().shape({
      shippingType: string().nullable(),
      shippingRate: object().when('shippingType', {
        is: SHIPPING_TYPE.SUPPLIER_ACCOUNT,
        then: object().required('This Field Is Required'),
        otherwise: object().notRequired(),
      }),
      customerAccount: string().when('shippingType', {
        is: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
        then: string()
          .required('This Field Is Required')
          .matches(
            /^[A-Za-z0-9_-]+$/,
            'Shipping account # can only contain letters, numbers, underscores, and hyphens',
          ),
        otherwise: string().notRequired(),
      }),
      customerCarrier: string().when('shippingType', {
        is: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
        then: string().required('This Field Is Required'),
        otherwise: string().notRequired(),
      }),
      method: string().when('shippingType', {
        is: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
        then: string().required('This Field Is Required'),
        otherwise: string().notRequired(),
      }),
      grouping: string().when('shippingType', {
        is: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
        then: string().required('This Field Is Required'),
        otherwise: string().notRequired(),
      }),
    }),
  });

  useEffect(() => {
    if (paymentMethod === null) setFieldValue('shippingType', null);
  }, [paymentMethod, setFieldValue]);

  const { data: shippingRatesData } = useGetShippingRates(
    {
      quoteKey: quote.digiQuoteKey,
      address: shippingAddress?.address,
      itemGroups: {
        default: Object.values(selectedOptions).map((option) => ({
          quantityId: option.quantity.id,
          expediteId: option.expedite ? option.expedite.id : null,
          addOnCells: [],
        })),
      },
    },
    {
      queryOptions: {
        enabled:
          shippingInformationType !== SHIPPING_INFO_TYPES.NONE &&
          paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
          isCalculatedShippingEnabled,
      },
    },
  );

  const shippingRates = shippingRatesData?.shippingRates.default;

  useEffect(() => {
    if (
      !shippingRatesData &&
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
      shippingType !== null &&
      (shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT ||
        values.shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT)
    )
      setFieldValue('shippingType', SHIPPING_TYPE.CUSTOMER_ACCOUNT);
  }, [
    shippingRatesData,
    setFieldValue,
    shippingType,
    values.shippingType,
    paymentMethod,
  ]);

  useEffect(() => {
    if (paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER)
      setFieldValue('shippingRate', SHIPPING_METHOD_RATES[0]);
  }, [paymentMethod, setFieldValue]);

  useEffect(() => {
    if (shippingRates && shippingRates.length > 0) {
      setFieldValue('shippingRate', shippingRates[0]);
    } else {
      setFieldValue('shippingRate', undefined);
    }
  }, [setFieldValue, shippingRates]);

  useImperativeHandle(ref, () => ({
    submit: () => submitForm(),
  }));

  return (
    <form autoComplete="none" onSubmit={handleSubmit}>
      <StyledRadioGroup
        name="shippingType"
        value={values.shippingType}
        onChange={(e) => {
          handleChange(e);
          if (
            e.target.value &&
            e.target.value !== SHIPPING_TYPE.SUPPLIER_ACCOUNT
          )
            setRate({ shippingRate: null });
          else if (
            e.target.value &&
            e.target.value === SHIPPING_TYPE.SUPPLIER_ACCOUNT
          )
            setRate({ shippingRate: values.shippingRate });
        }}
      >
        <StyledRadio value={null}>None</StyledRadio>
        {shippingInformationType !== SHIPPING_INFO_TYPES.NONE && (
          <>
            {isCalculatedShippingEnabled &&
              (shippingRatesData ||
                paymentMethod !== PAYMENT_METHOD.CREDIT_CARD) &&
              paymentMethod != null && (
                <StyledRadio value={SHIPPING_TYPE.SUPPLIER_ACCOUNT}>
                  {
                    SHIPPING_TYPE_LABEL[SHIPPING_TYPE.SUPPLIER_ACCOUNT][
                      paymentMethod
                    ]
                  }
                </StyledRadio>
              )}
            {paymentMethod !== null && (
              <StyledRadio value={SHIPPING_TYPE.CUSTOMER_ACCOUNT}>
                {SHIPPING_TYPE_LABEL[SHIPPING_TYPE.CUSTOMER_ACCOUNT]}
              </StyledRadio>
            )}
            {values.shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT && (
              <ShippingAccountForm
                values={values}
                handleChange={handleChange}
                touched={touched}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            )}
            <StyledRadio value={SHIPPING_TYPE.PICKUP}>
              {SHIPPING_TYPE_LABEL[SHIPPING_TYPE.PICKUP]}
            </StyledRadio>
          </>
        )}
      </StyledRadioGroup>
      <PaymentSummary />
      {values.shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT &&
        shippingInformationType !== SHIPPING_INFO_TYPES.NONE && (
          <>
            <DeliveryOptionsWrapper>
              {isFetchingShippingRates ? (
                <LoadingWrapper>
                  <Spinner large />
                </LoadingWrapper>
              ) : (
                <>
                  <DeliveryOptionsHeader>
                    Delivery Options
                  </DeliveryOptionsHeader>
                  <DeliveryOptions>
                    <StyledRadioGroup
                      name="shippingRate"
                      value={values.shippingRate}
                      onChange={(e) => {
                        handleChange(e);
                        setRate({ shippingRate: e.target.value });
                      }}
                    >
                      {paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
                      isCalculatedShippingEnabled
                        ? shippingRatesData?.shippingRates.default?.map(
                            (rate) => (
                              <StyledRadio key={rate.token} value={rate}>
                                <b>{`${toMoment(rate.arrivesOn)
                                  .utc()
                                  .format('dddd, MMM YY')}`}</b>{' '}
                                - Estimated delivery: by{' '}
                                {toMoment(rate.arrivesOn)
                                  .utc()
                                  .format('h:mm A')}
                                <br />
                                <ShippingRatePrice>
                                  {formatPrice(rate.price)} -{' '}
                                  {SHIPPING_METHOD_LABEL[rate.method]}
                                </ShippingRatePrice>
                              </StyledRadio>
                            ),
                          )
                        : SHIPPING_METHOD_RATES.map((rate) => (
                            <StyledRadio
                              key={`rate.method-${rate.method}`}
                              value={rate}
                            >
                              {SHIPPING_METHOD_LABEL[rate.method]}
                            </StyledRadio>
                          ))}
                    </StyledRadioGroup>
                  </DeliveryOptions>
                </>
              )}
            </DeliveryOptionsWrapper>
          </>
        )}
      <TotalsWrapper>
        <table>
          <tr>
            <LabelCell>Subtotal</LabelCell>
            <ValueCell>{`${formatPrice(subtotalCost, 2)}`}</ValueCell>
          </tr>
          <tr>
            <LabelCell>Tax</LabelCell>
            <ValueCell>{`${formatPrice(taxRate, 2)}`}</ValueCell>
          </tr>
          <tr>
            <LabelCell>Order Total</LabelCell>
            <ValueCell>{`${formatPrice(totalCost, 2)}`}</ValueCell>
          </tr>
        </table>
      </TotalsWrapper>
    </form>
  );
});

ShippingOptionsForm.displayName = 'ShippingOptionsForm';

export default ShippingOptionsForm;
